import { useState, useEffect, useCallback } from 'react';
import { fetchInstagramProfile, InstagramProfile } from '@/lib/instagram-service';
import { useToast } from '@/hooks/use-toast';
import { getSavedAccount } from '@/lib/saved-accounts-service';

interface UseProfileLoaderProps {
  username: string;
  onSuccess?: (data: InstagramProfile) => void;
  onError?: (error: Error) => void;
  onBack?: () => void;
}

export function useProfileLoader({
  username,
  onSuccess,
  onError,
  onBack
}: UseProfileLoaderProps) {
  const [profile, setProfile] = useState<InstagramProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState<string>('서버에서 데이터 요청 중...');
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [edgeFunctionCalled, setEdgeFunctionCalled] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // 로딩 상태가 아니면 아무것도 안 함
    if (!isLoading) return;

    // 로딩 단계 시뮬레이션 (UI 표시용)
    const stages = [
      '서버에서 데이터 요청 중...', // 0% ~
      '데이터 처리 중...',         // 25% ~
      '거의 완료되었습니다...'     // 50% ~ (새 단계 추가 또는 기존 단계 이름 변경)
    ];
    let currentStage = 0;
    let progress = loadingProgress; // 기존 진행률에서 시작하도록 수정
    const interval = setInterval(() => {
      // 실제 완료는 loadProfile에서 처리하므로 95%까지만 증가
      if (progress >= 95) {
        clearInterval(interval);
        return;
      }
      // 점진적으로 진행률 증가 (랜덤 요소 추가)
      progress += Math.random() * 2 + 0.5; // 증가 속도 조절 가능
      progress = Math.min(progress, 95); // 95%를 넘지 않도록

      // 진행률에 따라 단계 업데이트 (예: 33%, 66% 기준)
      const nextStageThreshold = (currentStage + 1) * (95 / stages.length);
      if (progress >= nextStageThreshold && currentStage < stages.length - 1) {
        currentStage++;
        setLoadingStage(stages[currentStage]);
      }
      setLoadingProgress(Math.floor(progress));
    }, 800); // 진행률 업데이트 간격 (조절 가능)

    // *** 핵심 수정: 타임아웃 설정 ***
    // 실제 fetchInstagramProfile의 타임아웃(3분=180초)보다 약간 길게 설정
    const timeoutDuration = 190000; // 3분 10초 (ms 단위)
    const timeoutId = setTimeout(() => {
      // 이 타임아웃은 fetchInstagramProfile 내부 타임아웃이 실패했거나
      // 다른 이유로 loadProfile이 제 시간 안에 완료되지 않았을 때 발동
      setError('데이터 로딩 시간이 초과되었습니다. 네트워크 상태를 확인하거나 잠시 후 다시 시도해주세요.');
      setIsLoading(false); // 로딩 상태 해제
      toast({
        title: "시간 초과",
        description: "데이터 로딩 시간이 초과되었습니다. 다시 시도해주세요.",
        variant: "destructive",
      });
      // 필요 시 onError 콜백 호출 (타임아웃 에러 전달)
      onError?.(new Error('데이터 로딩 시간이 초과되었습니다.'));
    }, timeoutDuration);

    // 클린업 함수: 컴포넌트 언마운트 또는 isLoading 변경 시 인터벌 및 타임아웃 해제
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
    // isLoading 상태 변경 시 effect 재실행 (toast는 안정적 참조로 가정하고 제외 가능)
  }, [isLoading, loadingProgress, onError, toast]); // loadingProgress를 의존성 배열에 추가

  // 프로필 로드 로직
  const loadProfile = useCallback(async () => {
    if (!username) return;

    // 초기 상태 설정
    setError(null);
    setIsLoading(true);

    // 로딩 프로그레스 리셋 방지: 전체 리셋 대신 낮은 값으로 설정
    if (loadingProgress < 5) {
      setLoadingProgress(5);
    }

    setLoadingStage('계정 정보 확인 중...');
    setRetryCount((prev) => prev + 1);
    setEdgeFunctionCalled(false);

    try {
      console.log('인스타그램 프로필 로드 시작:', username);

      // 1단계: public_instagram_accounts 테이블에서 계정 정보 확인
      // 진행률 유지를 위해 현재 로딩 프로그레스보다 높게 설정하되, 최소 10% 이상으로 설정
      setLoadingProgress(prev => Math.max(prev, 10));
      setLoadingStage('저장된 계정 정보 확인 중...');

      let savedAccount = await getSavedAccount(username);

      // 계정 정보가 있으면 바로 사용
      if (savedAccount && savedAccount.profile_data) {
        console.log('저장된 계정 정보를 발견했습니다:', username);
        setLoadingProgress(prev => Math.max(prev, 90));
        setLoadingStage('저장된 정보 로드 중...');

        // 프로필 데이터 매핑 및 사용
        const rp = savedAccount.profile_data;

        // 프로필 데이터를 InstagramProfile 형식으로 변환
        const mappedProfile: InstagramProfile = {
          biography: rp.biography || '',
          fullName: rp.fullName || rp.full_name || '',
          username: rp.username || username,
          profilePicUrl: rp.profilePicUrl || rp.profile_pic_url || '',
          profilePicUrlHD: rp.profilePicUrlHD || rp.profile_pic_url_hd || '',
          postsCount: Number(rp.postsCount || rp.posts_count || 0),
          followersCount: Number(rp.followersCount || rp.followers_count || 0),
          followsCount: Number(rp.followsCount || rp.follows_count || 0),
          isBusinessAccount: rp.isBusinessAccount || rp.is_business_account || false,
          isVerified: rp.isVerified || rp.is_verified || false,
          latestPosts: rp.latestPosts || rp.latest_posts || [],
          source: rp.source || 'saved_account'
        };

        setProfile(mappedProfile);
        setLoadingProgress(100);
        setLoadingStage('프로필 로드 완료');
        setIsLoading(false);
        onSuccess?.(mappedProfile);
        return;
      }

      // 2단계: 계정 정보가 없으면 엣지 펑션 호출
      console.log('저장된 계정 정보가 없습니다. 엣지 펑션 호출:', username);
      setLoadingProgress(prev => Math.max(prev, 20));
      setLoadingStage('서버에서 데이터 요청 중...');
      setEdgeFunctionCalled(true);

      // 엣지 펑션 호출 (데이터를 반환받지 않고 백그라운드에서 처리되도록)
      await fetchInstagramProfile(username, {
        fetchTimeout: 60000, // 60초 타임아웃 설정
        onProgress: (progress) => {
          // 진행도에 따라 20%~70% 사이의 진행도 표시
          // 현재 진행도보다 더 큰 값만 설정하도록 수정
          const mappedProgress = 20 + Math.round(progress * 0.5);
          setLoadingProgress(prev => Math.max(prev, mappedProgress));

          if (progress > 30 && progress <= 60) {
            setLoadingStage('데이터 처리 중...');
          } else if (progress > 60) {
            setLoadingStage('거의 완료되었습니다...');
          }
        }
      });

      // 3단계: 엣지 펑션 완료 후 public_instagram_accounts 테이블에서 데이터 대기
      setLoadingProgress(prev => Math.max(prev, 75));
      setLoadingStage('계정 정보 준비 중...');

      // 데이터가 테이블에 저장될 때까지 폴링
      let attempts = 0;
      const maxAttempts = 30; // 최대 30회 시도 (30초)

      while (attempts < maxAttempts) {
        attempts++;
        console.log(`저장된 계정 정보 확인 중... 시도 ${attempts}/${maxAttempts}`);

        // 진행도 조정 (75~95% 사이에서 서서히 증가)
        const progressIncrement = Math.min(20, Math.round(20 * (attempts / maxAttempts)));
        setLoadingProgress(prev => Math.max(prev, 75 + progressIncrement));

        // 데이터베이스에서 계정 정보 확인
        savedAccount = await getSavedAccount(username);

        if (savedAccount && savedAccount.profile_data) {
          console.log('엣지 펑션 처리 후 계정 정보를 발견했습니다:', username);

          // 프로필 데이터 매핑 및 사용
          const rp = savedAccount.profile_data;

          // 프로필 데이터를 InstagramProfile 형식으로 변환
          const mappedProfile: InstagramProfile = {
            biography: rp.biography || '',
            fullName: rp.fullName || rp.full_name || '',
            username: rp.username || username,
            profilePicUrl: rp.profilePicUrl || rp.profile_pic_url || '',
            profilePicUrlHD: rp.profilePicUrlHD || rp.profile_pic_url_hd || '',
            postsCount: Number(rp.postsCount || rp.posts_count || 0),
            followersCount: Number(rp.followersCount || rp.followers_count || 0),
            followsCount: Number(rp.followsCount || rp.follows_count || 0),
            isBusinessAccount: rp.isBusinessAccount || rp.is_business_account || false,
            isVerified: rp.isVerified || rp.is_verified || false,
            latestPosts: rp.latestPosts || rp.latest_posts || [],
            source: rp.source || 'saved_account'
          };

          setProfile(mappedProfile);
          setLoadingProgress(100);
          setLoadingStage('프로필 로드 완료');
          setIsLoading(false);
          onSuccess?.(mappedProfile);
          return;
        }

        // 1초 대기 후 다시 시도
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // 최대 시도 횟수를 초과하면 시간 초과 오류 발생
      throw new Error('계정 정보 로딩 시간 초과. 잠시 후 다시 시도해 주세요.');

    } catch (err: unknown) {
      // 에러 처리
      let errorMessage = "프로필을 불러오는데 실패했습니다.";

      let actualError: Error;

      if (err instanceof Error) {
        errorMessage = err.message;
        actualError = err;
      } else {
        actualError = new Error(String(err));
      }

      // Instagram 계정 미존재 처리
      if (errorMessage === 'not_found') {
        const msg = '존재하지 않는 계정입니다. 다시 시도해 주세요.';
        setError(msg);
        toast({ title: '오류', description: msg, variant: 'destructive' });
        // 일정 시간 후 검색 화면으로 돌아가기
        setTimeout(() => onBack?.(), 3000);
        setIsLoading(false);
        onError?.(new Error(msg));
        return;
      }

      // Failed to fetch 에러 명시적 처리
      if (errorMessage.includes('Failed to fetch')) {
        errorMessage = '네트워크 오류 또는 서버 연결 실패. 인터넷 연결을 확인하고 잠시 후에 다시 시도해주세요.';
      }

      // Timeout 에러 처리
      if (errorMessage.includes('시간 초과') || errorMessage.includes('timeout')) {
        errorMessage = '서버 응답 시간이 너무 깁니다. 잠시 후에 다시 시도해 주세요.';
      }

      setError(errorMessage);
      toast({
        title: "오류",
        description: errorMessage,
        variant: "destructive"
      });

      console.error('프로필 로드 오류:', errorMessage, actualError);
      setIsLoading(false); // 로딩 종료 (useEffect 클린업 트리거)
      onError?.(actualError); // 에러 콜백 호출
    }
    // useCallback 의존성 배열: 함수가 의존하는 값들 포함
  }, [username, toast, onBack, onSuccess, onError, loadingProgress]); // loadingProgress 의존성 추가

  // 자동 재시도 로직 추가
  const autoRetry = useCallback(() => {
    if (error && retryCount < 3) {
      toast({
        title: "자동 재시도",
        description: `프로필 로드를 다시 시도합니다. (${retryCount + 1}/3)`,
      });
      loadProfile();
    }
  }, [error, retryCount, loadProfile, toast]);

  // 에러가 발생하고 네트워크 오류인 경우 자동 재시도
  useEffect(() => {
    if (error && error.includes('네트워크 오류') && retryCount < 3) {
      const retryTimeout = setTimeout(() => {
        autoRetry();
      }, 3000); // 3초 후 재시도

      return () => clearTimeout(retryTimeout);
    }
  }, [error, retryCount, autoRetry]);

  // username이 변경될 때 loadProfile 함수 참조가 변경되고, effect가 다시 실행되어 프로필 로드
  useEffect(() => {
    if (username) { // username이 있을 때만 로드
      loadProfile();
    } else {
      // username이 없으면 상태 초기화
      setIsLoading(false);
      setProfile(null);
      setError(null);
      setLoadingProgress(0);
      setLoadingStage('서버에서 데이터 요청 중...');
    }
    // loadProfile 함수 자체가 변경될 때 (주로 username, onBack, onSuccess, onError 변경 시) 재실행
  }, [loadProfile, username]); // username도 의존성에 추가하여 username 변경 시 즉시 재로드 보장

  // 훅 반환 값
  return {
    profile,
    isLoading,
    loadingProgress,
    loadingStage,
    error,
    loadProfile, // 외부에서 수동 재시도 등을 위해 반환
    retryCount,
    edgeFunctionCalled
  };
}